import React, { useRef, useState, useEffect } from "react";
import { Text, TextProps } from "@/components/ui/text";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger, TooltipArrow } from "@/components/ui/tooltip";

interface TruncatedTextProps extends Omit<TextProps, "children"> {
  /** Plain text to display. Use when you don't need HTML/formatting. */
  text?: string;
  /** React node (HTML/JSX) to display. Use for bold labels + text, etc. When provided, takes precedence over text. */
  children?: React.ReactNode;
  maxLines?: number;
  tooltipMaxWidth?: string;
}

/**
 * Component to show text with line clamp and tooltip only if truncated.
 * Accepts either plain `text` or `children` (HTML/JSX) for formatted content.
 * @param text - Plain text to display (optional if children is used)
 * @param children - React node for formatted content, e.g. <><span className="font-semibold">Label: </span><span>{value}</span></>
 * @param maxLines - Maximum number of lines (default: 2)
 * @param tooltipMaxWidth - Max width for tooltip (default: "max-w-md")
 * @param ...textProps - All other Text component props (size, weight, color, className, etc.)
 */
export const TruncatedText: React.FC<TruncatedTextProps> = ({
  text,
  children,
  maxLines = 2,
  tooltipMaxWidth = "max-w-md",
  className = "",
  ...textProps
}) => {
  const textRef = useRef<HTMLElement>(null);
  const [isTruncated, setIsTruncated] = useState(false);
  const content = children ?? text;

  useEffect(() => {
    const checkTruncation = () => {
      if (textRef.current) {
        const element = textRef.current;
        // Check if text is truncated by comparing scroll height to client height
        // Add a small threshold (1px) to account for rounding differences
        const isTextTruncated = element.scrollHeight > element.clientHeight + 1;
        setIsTruncated(isTextTruncated);
      }
    };

    // Use multiple delays to ensure DOM and styles are fully applied
    let timeoutId1: NodeJS.Timeout;
    let timeoutId2: NodeJS.Timeout;

    // First check after initial render
    timeoutId1 = setTimeout(checkTruncation, 0);

    // Second check after a longer delay to ensure styles are applied
    timeoutId2 = setTimeout(checkTruncation, 100);

    // Also check on next animation frame
    const rafId = requestAnimationFrame(() => {
      setTimeout(checkTruncation, 0);
    });

    window.addEventListener("resize", checkTruncation);
    return () => {
      cancelAnimationFrame(rafId);
      if (timeoutId1) clearTimeout(timeoutId1);
      if (timeoutId2) clearTimeout(timeoutId2);
      window.removeEventListener("resize", checkTruncation);
    };
  }, [text, maxLines, children]);

  if (content == null || content === "") return null;

  // Always apply line-clamp styles using inline styles to ensure it works
  // Tailwind classes may not be available, so we use inline styles for reliability
  const lineClampStyle: React.CSSProperties = {
    display: "-webkit-box",
    WebkitLineClamp: maxLines,
    WebkitBoxOrient: "vertical" as const,
    overflow: "hidden",
    textOverflow: "ellipsis",
  };

  // Ensure min-width is 0 for flex containers to allow proper truncation
  const finalClassName = `min-w-0 ${className}`.trim();

  const textElement = (
    <Text
      ref={textRef}
      as={textProps.as || "p"}
      size={textProps.size || "sm"}
      weight={textProps.weight || "normal"}
      color={textProps.color || "default"}
      className={finalClassName}
      style={lineClampStyle}
      {...textProps}
    >
      {content}
    </Text>
  );

  if (isTruncated) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            {textElement}
          </TooltipTrigger>
          <TooltipContent className={tooltipMaxWidth}>
            <TooltipArrow />
            {typeof content === "string" ? <p>{content}</p> : <div>{content}</div>}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  return textElement;
};

